import { addIcons, addCachedAssets } from '@larva.io/webcomponents';
export * from '@larva.io/webcomponents';
import { applyPolyfills, defineCustomElements } from '@larva.io/webcomponents/loader';
import { __rest } from 'tslib';
import React, { createElement } from 'react';
import 'react-dom';

const dashToPascalCase = (str) => str
    .toLowerCase()
    .split('-')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join('');
const camelToDashCase = (str) => str.replace(/([A-Z])/g, (m) => `-${m[0].toLowerCase()}`);

const attachProps = (node, newProps, oldProps = {}) => {
    // some test frameworks don't render DOM elements, so we test here to make sure we are dealing with DOM first
    if (node instanceof Element) {
        // add any classes in className to the class list
        const className = getClassName(node.classList, newProps, oldProps);
        if (className !== '') {
            node.className = className;
        }
        Object.keys(newProps).forEach((name) => {
            if (name === 'children' ||
                name === 'style' ||
                name === 'ref' ||
                name === 'class' ||
                name === 'className' ||
                name === 'forwardedRef') {
                return;
            }
            if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                const eventName = name.substring(2);
                const eventNameLc = eventName[0].toLowerCase() + eventName.substring(1);
                if (!isCoveredByReact(eventNameLc)) {
                    syncEvent(node, eventNameLc, newProps[name]);
                }
            }
            else {
                node[name] = newProps[name];
                const propType = typeof newProps[name];
                if (propType === 'string') {
                    node.setAttribute(camelToDashCase(name), newProps[name]);
                }
            }
        });
    }
};
const getClassName = (classList, newProps, oldProps) => {
    const newClassProp = newProps.className || newProps.class;
    const oldClassProp = oldProps.className || oldProps.class;
    // map the classes to Maps for performance
    const currentClasses = arrayToMap(classList);
    const incomingPropClasses = arrayToMap(newClassProp ? newClassProp.split(' ') : []);
    const oldPropClasses = arrayToMap(oldClassProp ? oldClassProp.split(' ') : []);
    const finalClassNames = [];
    // loop through each of the current classes on the component
    // to see if it should be a part of the classNames added
    currentClasses.forEach((currentClass) => {
        if (incomingPropClasses.has(currentClass)) {
            // add it as its already included in classnames coming in from newProps
            finalClassNames.push(currentClass);
            incomingPropClasses.delete(currentClass);
        }
        else if (!oldPropClasses.has(currentClass)) {
            // add it as it has NOT been removed by user
            finalClassNames.push(currentClass);
        }
    });
    incomingPropClasses.forEach((s) => finalClassNames.push(s));
    return finalClassNames.join(' ');
};
/**
 * Checks if an event is supported in the current execution environment.
 * @license Modernizr 3.0.0pre (Custom Build) | MIT
 */
const isCoveredByReact = (eventNameSuffix) => {
    if (typeof document === 'undefined') {
        return true;
    }
    else {
        const eventName = 'on' + eventNameSuffix;
        let isSupported = eventName in document;
        if (!isSupported) {
            const element = document.createElement('div');
            element.setAttribute(eventName, 'return;');
            isSupported = typeof element[eventName] === 'function';
        }
        return isSupported;
    }
};
const syncEvent = (node, eventName, newEventHandler) => {
    const eventStore = node.__events || (node.__events = {});
    const oldEventHandler = eventStore[eventName];
    // Remove old listener so they don't double up.
    if (oldEventHandler) {
        node.removeEventListener(eventName, oldEventHandler);
    }
    // Bind new listener.
    node.addEventListener(eventName, (eventStore[eventName] = function handler(e) {
        if (newEventHandler) {
            newEventHandler.call(this, e);
        }
    }));
};
const arrayToMap = (arr) => {
    const map = new Map();
    arr.forEach((s) => map.set(s, s));
    return map;
};

const setRef = (ref, value) => {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref != null) {
        // Cast as a MutableRef so we can assign current
        ref.current = value;
    }
};
const mergeRefs = (...refs) => {
    return (value) => {
        refs.forEach((ref) => {
            setRef(ref, value);
        });
    };
};
const createForwardRef = (ReactComponent, displayName) => {
    const forwardRef = (props, ref) => {
        return React.createElement(ReactComponent, Object.assign({}, props, { forwardedRef: ref }));
    };
    forwardRef.displayName = displayName;
    return React.forwardRef(forwardRef);
};

const createReactComponent = (tagName, ReactComponentContext, manipulatePropsFunction, defineCustomElement) => {
    if (defineCustomElement !== undefined) {
        defineCustomElement();
    }
    const displayName = dashToPascalCase(tagName);
    const ReactComponent = class extends React.Component {
        constructor(props) {
            super(props);
            this.setComponentElRef = (element) => {
                this.componentEl = element;
            };
        }
        componentDidMount() {
            this.componentDidUpdate(this.props);
        }
        componentDidUpdate(prevProps) {
            attachProps(this.componentEl, this.props, prevProps);
        }
        render() {
            const _a = this.props, { children, forwardedRef, style, className, ref } = _a, cProps = __rest(_a, ["children", "forwardedRef", "style", "className", "ref"]);
            let propsToPass = Object.keys(cProps).reduce((acc, name) => {
                const value = cProps[name];
                if (name.indexOf('on') === 0 && name[2] === name[2].toUpperCase()) {
                    const eventName = name.substring(2).toLowerCase();
                    if (typeof document !== 'undefined' && isCoveredByReact(eventName)) {
                        acc[name] = value;
                    }
                }
                else {
                    // we should only render strings, booleans, and numbers as attrs in html.
                    // objects, functions, arrays etc get synced via properties on mount.
                    const type = typeof value;
                    if (type === 'string' || type === 'boolean' || type === 'number') {
                        acc[camelToDashCase(name)] = value;
                    }
                }
                return acc;
            }, {});
            if (manipulatePropsFunction) {
                propsToPass = manipulatePropsFunction(this.props, propsToPass);
            }
            const newProps = Object.assign(Object.assign({}, propsToPass), { ref: mergeRefs(forwardedRef, this.setComponentElRef), style });
            /**
             * We use createElement here instead of
             * React.createElement to work around a
             * bug in Vite (https://github.com/vitejs/vite/issues/6104).
             * React.createElement causes all elements to be rendered
             * as <tagname> instead of the actual Web Component.
             */
            return createElement(tagName, newProps, children);
        }
        static get displayName() {
            return displayName;
        }
    };
    // If context was passed to createReactComponent then conditionally add it to the Component Class
    if (ReactComponentContext) {
        ReactComponent.contextType = ReactComponentContext;
    }
    return createForwardRef(ReactComponent, displayName);
};

/* eslint-disable */
const LarAnalogValue = /*@__PURE__*/ createReactComponent('lar-analog-value');
const LarApp = /*@__PURE__*/ createReactComponent('lar-app');
const LarArea = /*@__PURE__*/ createReactComponent('lar-area');
const LarAreaBypass = /*@__PURE__*/ createReactComponent('lar-area-bypass');
const LarAreaContent = /*@__PURE__*/ createReactComponent('lar-area-content');
const LarAreaTroubles = /*@__PURE__*/ createReactComponent('lar-area-troubles');
const LarAreaZones = /*@__PURE__*/ createReactComponent('lar-area-zones');
const LarBackdrop = /*@__PURE__*/ createReactComponent('lar-backdrop');
const LarBadge = /*@__PURE__*/ createReactComponent('lar-badge');
const LarButton = /*@__PURE__*/ createReactComponent('lar-button');
const LarButtonGroup = /*@__PURE__*/ createReactComponent('lar-button-group');
const LarButtonPush = /*@__PURE__*/ createReactComponent('lar-button-push');
const LarDebug = /*@__PURE__*/ createReactComponent('lar-debug');
const LarDebugContent = /*@__PURE__*/ createReactComponent('lar-debug-content');
const LarDoor = /*@__PURE__*/ createReactComponent('lar-door');
const LarDoorContent = /*@__PURE__*/ createReactComponent('lar-door-content');
const LarIcon = /*@__PURE__*/ createReactComponent('lar-icon');
const LarInnerRangeArea = /*@__PURE__*/ createReactComponent('lar-inner-range-area');
const LarInnerRangeAreaDoorContent = /*@__PURE__*/ createReactComponent('lar-inner-range-area-door-content');
const LarInnerRangeDoor = /*@__PURE__*/ createReactComponent('lar-inner-range-door');
const LarInnerRangeInceptionArea = /*@__PURE__*/ createReactComponent('lar-inner-range-inception-area');
const LarInnerRangeInceptionAreaContent = /*@__PURE__*/ createReactComponent('lar-inner-range-inception-area-content');
const LarInnerRangeInceptionDoor = /*@__PURE__*/ createReactComponent('lar-inner-range-inception-door');
const LarInnerRangeInceptionDoorContent = /*@__PURE__*/ createReactComponent('lar-inner-range-inception-door-content');
const LarIntercom = /*@__PURE__*/ createReactComponent('lar-intercom');
const LarIntercomContent = /*@__PURE__*/ createReactComponent('lar-intercom-content');
const LarKoneElevators = /*@__PURE__*/ createReactComponent('lar-kone-elevators');
const LarKoneElevatorsContent = /*@__PURE__*/ createReactComponent('lar-kone-elevators-content');
const LarList = /*@__PURE__*/ createReactComponent('lar-list');
const LarListHeader = /*@__PURE__*/ createReactComponent('lar-list-header');
const LarListItem = /*@__PURE__*/ createReactComponent('lar-list-item');
const LarLogModal = /*@__PURE__*/ createReactComponent('lar-log-modal');
const LarModal = /*@__PURE__*/ createReactComponent('lar-modal');
const LarNode = /*@__PURE__*/ createReactComponent('lar-node');
const LarNodeLinkedComponents = /*@__PURE__*/ createReactComponent('lar-node-linked-components');
const LarNodeMoreButtons = /*@__PURE__*/ createReactComponent('lar-node-more-buttons');
const LarNodeTitles = /*@__PURE__*/ createReactComponent('lar-node-titles');
const LarNotify = /*@__PURE__*/ createReactComponent('lar-notify');
const LarOnoff = /*@__PURE__*/ createReactComponent('lar-onoff');
const LarOnoffContent = /*@__PURE__*/ createReactComponent('lar-onoff-content');
const LarPushButton = /*@__PURE__*/ createReactComponent('lar-push-button');
const LarPushButtonContent = /*@__PURE__*/ createReactComponent('lar-push-button-content');
const LarRadioButton = /*@__PURE__*/ createReactComponent('lar-radio-button');
const LarRadioButtonContent = /*@__PURE__*/ createReactComponent('lar-radio-button-content');
const LarRange = /*@__PURE__*/ createReactComponent('lar-range');
const LarRtspH264Mjpeg = /*@__PURE__*/ createReactComponent('lar-rtsp-h264-mjpeg');
const LarSlider = /*@__PURE__*/ createReactComponent('lar-slider');
const LarSlidingDoor = /*@__PURE__*/ createReactComponent('lar-sliding-door');
const LarToggle = /*@__PURE__*/ createReactComponent('lar-toggle');
const LarTranslate = /*@__PURE__*/ createReactComponent('lar-translate');
const LarTwoPosController = /*@__PURE__*/ createReactComponent('lar-two-pos-controller');
const LarUtilityLog = /*@__PURE__*/ createReactComponent('lar-utility-log');
const LarUtilityMeter = /*@__PURE__*/ createReactComponent('lar-utility-meter');
const LarVideoModal = /*@__PURE__*/ createReactComponent('lar-video-modal');
const LarWeather = /*@__PURE__*/ createReactComponent('lar-weather');
const LarWeatherContent = /*@__PURE__*/ createReactComponent('lar-weather-content');
const LarWeatherItem = /*@__PURE__*/ createReactComponent('lar-weather-item');
const RangeContent = /*@__PURE__*/ createReactComponent('range-content');
const SlidingDoorContent = /*@__PURE__*/ createReactComponent('sliding-door-content');
const TwoPosControllerContent = /*@__PURE__*/ createReactComponent('two-pos-controller-content');

const icons = require('@larva.io/webcomponents/dist/icons.json');
async function loadIcons(iconList) {
    const iconPaths = {};
    if (iconList && iconList.icons && Array.isArray(iconList.icons)) {
        for (const svg of iconList.icons) {
            const path = await import(/* webpackMode: "lazy-once" */ `@larva.io/webcomponents/dist/collection/components/internal/icon/assets/svg/${svg}.svg`);
            if (path) {
                iconPaths[svg] = path.default;
            }
        }
    }
    addIcons(iconPaths);
}
async function loadAssets() {
    const assetsPaths = {};
    // add custom assets
    assetsPaths['doorbell.mp3'] = require(`@larva.io/webcomponents/dist/collection/components/intercom/assets/doorbell.mp3`);
    addCachedAssets(assetsPaths);
}
loadIcons(icons).then(() => {
    return loadAssets();
}).then(() => {
    return applyPolyfills();
}).then(() => {
    return defineCustomElements();
}).catch(err => {
    console.error(err);
});
const whenDefined = customElements.whenDefined;
function isReady() {
    return customElements.whenDefined('lar-app');
}

export { LarAnalogValue, LarApp, LarArea, LarAreaBypass, LarAreaContent, LarAreaTroubles, LarAreaZones, LarBackdrop, LarBadge, LarButton, LarButtonGroup, LarButtonPush, LarDebug, LarDebugContent, LarDoor, LarDoorContent, LarIcon, LarInnerRangeArea, LarInnerRangeAreaDoorContent, LarInnerRangeDoor, LarInnerRangeInceptionArea, LarInnerRangeInceptionAreaContent, LarInnerRangeInceptionDoor, LarInnerRangeInceptionDoorContent, LarIntercom, LarIntercomContent, LarKoneElevators, LarKoneElevatorsContent, LarList, LarListHeader, LarListItem, LarLogModal, LarModal, LarNode, LarNodeLinkedComponents, LarNodeMoreButtons, LarNodeTitles, LarNotify, LarOnoff, LarOnoffContent, LarPushButton, LarPushButtonContent, LarRadioButton, LarRadioButtonContent, LarRange, LarRtspH264Mjpeg, LarSlider, LarSlidingDoor, LarToggle, LarTranslate, LarTwoPosController, LarUtilityLog, LarUtilityMeter, LarVideoModal, LarWeather, LarWeatherContent, LarWeatherItem, RangeContent, SlidingDoorContent, TwoPosControllerContent, isReady, whenDefined };
//# sourceMappingURL=index.esm.js.map
